import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff, LockReset } from "@mui/icons-material";

import api from "../../services/api";
import { updateToken } from "../../store/modules/auth/actions";
import { updateProfileSuccess } from "../../store/modules/user/actions";

// Tela de troca de senha obrigatória após reset (senha temporária).
// Conclui o fluxo: envia a senha atual (temporária) + a nova, recebe um token
// novo (token_version atualizado) e libera o sistema. (remediação StealC — W4)
export default function ChangePassword() {
  const dispatch = useDispatch();
  const history = useHistory();
  const profile = useSelector((state) => state.user.profile);
  const signed = useSelector((state) => state.auth.signed);

  const isEmpresa = !!profile?.cnpj;
  const endpoint = isEmpresa
    ? "/empresa/change-password"
    : "/aluno/change-password";

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // O `history.push` ficava aqui, no corpo do render: navegar durante o render
  // dispara re-render, que empurra de novo -- loop infinito, React error #185
  // ("Maximum update depth exceeded"), e a tela nao pinta. O redirecionamento
  // tem de ser efeito colateral, nao render. (o mesmo bug esta em producao)
  useEffect(() => {
    if (!signed) history.push("/login");
  }, [signed, history]);

  if (!signed) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (next.length < 8) {
      setError("A nova senha deve ter ao menos 8 caracteres.");
      return;
    }
    if (next !== confirm) {
      setError("A confirmação não confere com a nova senha.");
      return;
    }
    if (next === current) {
      setError("A nova senha deve ser diferente da temporária.");
      return;
    }

    setLoading(true);
    try {
      // trim nos dois: a senha temporária chega por e-mail e o copiar/colar
      // costuma levar espaço/quebra de linha grudada, o que fazia o backend
      // responder 401 "Senha atual incorreta." com a senha certa digitada.
      // O SignIn também não faz trim na senha, então trimar a nova evita que a
      // pessoa fique com uma senha que ela não consegue mais usar no login.
      const { data } = await api.patch(endpoint, {
        current_password: current.trim(),
        new_password: next.trim(),
      });

      // O backend devolve um token novo (token_version atualizado). Sem trocar,
      // o token atual seria rejeitado no próximo request.
      if (data?.token) {
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        dispatch(updateToken(data.token));
      }
      if (profile) {
        dispatch(updateProfileSuccess({ ...profile, must_change_password: false }));
      }

      history.push(isEmpresa ? "/dashboard" : "/dashboard");
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Não foi possível alterar a senha. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{ p: { xs: 3, sm: 4 }, maxWidth: 440, width: "100%", borderRadius: 3 }}
      >
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <LockReset sx={{ fontSize: 44, color: "#1A3C6B" }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1A3C6B", mt: 1 }}>
            Defina uma nova senha
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Você entrou com uma senha temporária. Por segurança, crie uma nova
            senha para continuar.
          </Typography>
        </Box>

        {error ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            margin="normal"
            label="Senha temporária (atual)"
            type={show ? "text" : "password"}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            autoComplete="current-password"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Nova senha"
            type={show ? "text" : "password"}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            helperText="Mínimo de 8 caracteres."
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShow((v) => !v)} edge="end">
                    {show ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Confirmar nova senha"
            type={show ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ mt: 3, py: 1.2, bgcolor: "#1A3C6B", "&:hover": { bgcolor: "#12305a" } }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Salvar nova senha"}
          </Button>
        </form>
      </Paper>
    </Box>
  );
}
